import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getOpportunities } from "../../apis/opportunities";
import { normalizeOpportunitiesResponse } from "../../utils/normalizeOpportunity";
import {
    MAX_OPPORTUNITIES,
    isOpportunitySelected,
    toggleOpportunitySelection,
    removeOpportunity,
} from "../../utils/opportunitySelection";
import { useCursorPaginatedList, mergeById } from "../../hooks/useCursorPaginatedList";
import { startCampaignFromOpportunity } from "../../store/campaignBuilderSlice";
import { selectActiveNiche } from "../../store/nicheSlice";
import { APP_ROUTES, buildCampaignWizardPath } from "../../utils/campaignWizardNavigation";
import ListScrollFooter from "../shared/ListScrollFooter";
import { OpportunityCardCompact } from "../shared/OpportunityCard";
import OpportunityListSkeletons from "./OpportunityListSkeletons";
import OpportunitySelectionFooter from "./OpportunitySelectionFooter";
import PageHeader, { PageShell } from "../shared/PageHeader";
import GuestHomeBanner from "../dashboard/GuestHomeBanner";
import { useAuthModal, REGISTER_BUSINESS_AUTH_MODAL } from "../../context/AuthModalContext";
import {
    CONTENT_AI_PAGE_SEO,
    buildContentAiStructuredData,
} from "../../utils/pageMeta";
import "bootstrap-icons/font/bootstrap-icons.css";
import {
    trackCtaClicked,
    trackItemSelected,
    trackFeatureStarted,
} from "../../analytics/analytics";
import { useScrolled } from "../../analytics/analytics.hooks";
import {
    ANALYTICS_ITEM_TYPES,
    ANALYTICS_SECTIONS,
    ANALYTICS_CTAS,
    ANALYTICS_FEATURES,
    ANALYTICS_ENTRY_POINTS,
} from "../../analytics/analytics.constants";

const INITIAL_SKELETON_COUNT = 6;
const LOAD_MORE_SKELETON_COUNT = 4;

const SCOPE_FILTERS = [
    { id: "all", label: "All", icon: "bi-grid-3x3-gap-fill" },
    { id: "global", label: "Global", icon: "bi-globe-americas" },
    { id: "industry", label: "Industry", icon: "bi-building" },
];

const SORT_OPTIONS = [
    { id: "default", label: "Newest", icon: "bi-clock-history" },
    { id: "relevance_desc", label: "Relevance", icon: "bi-graph-up-arrow" },
];

function FilterPill({ icon, label, isActive, onClick, disabled = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 sm:gap-1.5 sm:px-4 sm:py-2 sm:text-sm ${isActive
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200/80 hover:text-slate-900 disabled:hover:bg-slate-100 disabled:hover:text-slate-600"
                }`}
            aria-pressed={isActive}
            aria-disabled={disabled}
        >
            <i className={`bi ${icon}`} aria-hidden />
            <span>{label}</span>
        </button>
    );
}

function OpportunityControls({ scope, onScopeChange, sort, onSortChange, isGuest = false }) {
    return (
        <div className="my-12 flex min-w-0 w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-1.5 sm:flex-wrap sm:gap-2">
                <span className="me-0.5 shrink-0 text-xs font-bold uppercase tracking-wide text-slate-400 sm:me-1">
                    Source
                </span>
                {SCOPE_FILTERS.map((filter) => (
                    <FilterPill
                        key={filter.id}
                        icon={filter.icon}
                        label={filter.label}
                        isActive={scope === filter.id}
                        disabled={isGuest && filter.id !== "global"}
                        onClick={() => onScopeChange(filter.id)}
                    />
                ))}
            </div>

            <div className="flex min-w-0 items-center gap-1.5 sm:flex-wrap sm:gap-2">
                <span className="me-0.5 shrink-0 text-xs font-bold uppercase tracking-wide text-slate-400 sm:me-1">
                    Sort
                </span>
                {SORT_OPTIONS.map((option) => (
                    <FilterPill
                        key={option.id}
                        icon={option.icon}
                        label={option.label}
                        isActive={sort === option.id}
                        onClick={() => onSortChange(option.id)}
                    />
                ))}
            </div>
        </div>
    );
}

function filterOpportunitiesBySearch(opportunities, query) {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return opportunities;

    return opportunities.filter((item) => {
        const title = String(item.title ?? "").toLowerCase();
        const opportunity = String(item.opportunity ?? "").toLowerCase();
        return title.includes(trimmed) || opportunity.includes(trimmed);
    });
}

function getEmptyMessage({ scope, search }) {
    if (search.trim()) {
        return "No trends match your search.";
    }
    if (scope === "global") {
        return "No global trends right now.";
    }
    if (scope === "industry") {
        return "No industry trends right now.";
    }
    return "No trends right now — check back soon.";
}

function OpportunitySearchBar({ value, onChange }) {
    return (
        <div className="relative mb-4">
            <i
                className="bi bi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                aria-hidden
            />
            <input
                type="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Search trends by title…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/20"
            />
        </div>
    );
}

export default function ContentIntelligence() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { openAuthModal } = useAuthModal();
    const userId = useSelector((state) => state.auth.user?.uid);
    const activeNiche = useSelector(selectActiveNiche);
    const isGuest = !userId;
    const [scope, setScope] = useState(isGuest ? "global" : "all");
    const [sort, setSort] = useState("default");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState([]);
    const [expandedOpportunityId, setExpandedOpportunityId] = useState(null);
    const opportunitiesSectionRef = useRef(null);

    useEffect(() => {
        if (isGuest) {
            setScope("global");
        }
    }, [isGuest]);

    const isMaxReached = selected.length >= MAX_OPPORTUNITIES;
    const hasSelection = selected.length > 0;

    const fetchOpportunities = useCallback(
        (params) =>
            getOpportunities({
                ...params,
                sourceType: scope,
                sort,
                ...(activeNiche ? { niche: activeNiche } : {}),
            }),
        [scope, sort, activeNiche]
    );

    const {
        items: opportunities,
        hasMore,
        loading,
        loadingMore,
        error,
        loadMoreRef,
    } = useCursorPaginatedList({
        fetchFn: fetchOpportunities,
        normalizeResponse: normalizeOpportunitiesResponse,
        mergeItems: mergeById,
    });

    const displayedOpportunities = useMemo(
        () => filterOpportunitiesBySearch(opportunities, search),
        [opportunities, search]
    );

    const emptyMessage = getEmptyMessage({ scope, search });

    useScrolled(
        opportunitiesSectionRef,
        ANALYTICS_SECTIONS.OPPORTUNITIES,
        !loading && displayedOpportunities.length > 0
    );

    const handleSelectForCampaign = (opportunity, position) => {
        const wasSelected = isOpportunitySelected(selected, opportunity.id);
        setSelected((prev) => toggleOpportunitySelection(prev, opportunity));

        if (!wasSelected) {
            trackItemSelected(
                ANALYTICS_ITEM_TYPES.OPPORTUNITY,
                opportunity.id,
                position,
                { item_title: opportunity.title || opportunity.opportunity || "" }
            );
        }
    };

    const handleRemoveSelected = (id) => {
        setSelected((prev) => removeOpportunity(prev, id));
    };

    const handleStartCampaign = () => {
        if (selected.length === 0) return;

        trackCtaClicked(ANALYTICS_CTAS.START_CAMPAIGN);
        trackFeatureStarted(ANALYTICS_FEATURES.CAMPAIGN_BUILDER, {
            entry_point: ANALYTICS_ENTRY_POINTS.CONTENT_AI,
        });

        dispatch(startCampaignFromOpportunity({ opportunities: selected, userId }));
        navigate(buildCampaignWizardPath(APP_ROUTES.CONTENT_AI, null, { fastForward: true }));
    };

    return (
        <PageShell className={`${hasSelection ? "pb-28 md:pb-24" : ""}`.trim()}>


            <PageHeader
                title="Top Marketing Trends"
                metaTitle={CONTENT_AI_PAGE_SEO.title}
                metaDescription={CONTENT_AI_PAGE_SEO.description}
                metaKeywords={CONTENT_AI_PAGE_SEO.keywords}
                path={CONTENT_AI_PAGE_SEO.path}
                imagePath={CONTENT_AI_PAGE_SEO.imagePath}
                imageAlt={CONTENT_AI_PAGE_SEO.imageAlt}
                structuredData={buildContentAiStructuredData()}
                description="Important trends your business should post about."
            >
                <span className="inline-flex items-center rounded-full bg-gradient-to-r from-rose-50 to-orange-50 px-2 py-0.5 text-xs font-medium text-rose-700">
                    Powered by AI <i className="bi bi-sparkles text-yellow-500"></i>
                </span>
            </PageHeader>

            {/* {isGuest ? (
                <GuestHomeBanner
                    title="Get personalized AI marketing trends"
                    description="Register your business to unlock trends tailored to your industry, relevance sorting, and more."
                    onCtaClick={() => openAuthModal(REGISTER_BUSINESS_AUTH_MODAL)}
                    ctaLabel="Register your business"
                    ctaAnalyticsId={ANALYTICS_CTAS.REGISTER_BUSINESS}
                    ctaIconClass="bi-building"
                    showSocialIcons={false}
                />
            ) : null} */}

            <OpportunitySearchBar value={search} onChange={setSearch} />

            <OpportunityControls
                scope={scope}
                onScopeChange={setScope}
                sort={sort}
                onSortChange={setSort}
                isGuest={isGuest}
            />

            {error ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                    <p className="mb-0 font-semibold">Could not load trends</p>
                    <p className="mt-1 text-sm">{error}</p>
                </div>
            ) : null}

            <div ref={opportunitiesSectionRef} className=" min-w-0 md:mx-0">
                {loading ? (
                    <OpportunityListSkeletons
                        count={INITIAL_SKELETON_COUNT}
                        label="Finding trends"
                    />
                ) : displayedOpportunities.length === 0 ? (
                    <p className="text-center text-sm text-slate-500">{emptyMessage}</p>
                ) : (
                    <div
                        className={`grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 ${expandedOpportunityId != null ? "items-start" : "items-stretch"
                            }`}
                    >
                        {displayedOpportunities.map((item, index) => {
                            const id = item.id ?? index;
                            const isSelected = isOpportunitySelected(selected, item.id);
                            const isExpanded = expandedOpportunityId === id;

                            return (
                                <div
                                    key={id}
                                    className={`col-span-1 w-full ${isExpanded ? "self-start" : ""}`}
                                >
                                    <OpportunityCardCompact
                                        className="w-full"
                                        opportunity={item}
                                        selectable
                                        isSelected={isSelected}
                                        disabled={isMaxReached && !isSelected}
                                        onToggle={() => handleSelectForCampaign(item, index + 1)}
                                        showActions
                                        isExpanded={isExpanded}
                                        onExpandedChange={(open) =>
                                            setExpandedOpportunityId(open ? id : null)
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}

            </div>

            {loadingMore ? (
                <OpportunityListSkeletons
                    count={LOAD_MORE_SKELETON_COUNT}
                    label="Loading more trends"
                />
            ) : null}

            <ListScrollFooter
                loadMoreRef={loadMoreRef}
                showAllShown={
                    !loading && !loadingMore && opportunities.length > 0 && !hasMore
                }
            />

            <OpportunitySelectionFooter
                selected={selected}
                onRemove={handleRemoveSelected}
                onStartCampaign={handleStartCampaign}
            />
        </PageShell>
    );
}
